import type { AIAction } from '@aibi/contracts';
import { sanitizeMessage } from '@aibi/shared';
import type { AIErrorCode } from '../errors.js';

export const AI_HISTORY_STATUSES = [
  'proposed',
  'applied',
  'dismissed',
  'clarification',
  'explanation',
  'suggestion',
  'rejected',
  'cancelled',
] as const;
export type AIHistoryStatus = (typeof AI_HISTORY_STATUSES)[number];

/**
 * Registro de una interaccion con la IA. Desacoplado de la persistencia definitiva:
 * guarda solo el prompt redactado, las acciones normalizadas, codigos y huellas.
 */
export interface AIHistoryEntry {
  readonly requestId: string;
  readonly timestamp: string;
  readonly providerId: string;
  readonly prompt: string;
  readonly status: AIHistoryStatus;
  readonly actions: readonly AIAction[];
  readonly diagnosticCodes: readonly AIErrorCode[];
  readonly contextFingerprint: string | null;
}

export interface AIHistory {
  record(entry: AIHistoryEntry): void;
  /** Cambia el estado de una entrada (por ejemplo, `proposed` -> `applied`). */
  updateStatus(requestId: string, status: AIHistoryStatus): void;
  list(): readonly AIHistoryEntry[];
  clear(): void;
}

const MAX_PROMPT_LENGTH = 500;
export const REDACTED_PROMPT = '[contenido no permitido]';

/** Prompt apto para historial: sin secretos reconocibles y acotado. */
export function redactPrompt(prompt: string, unsafe: boolean): string {
  if (unsafe) return REDACTED_PROMPT;
  return sanitizeMessage(prompt).slice(0, MAX_PROMPT_LENGTH);
}

/**
 * Historial en memoria con tamano acotado (las entradas mas antiguas se descartan).
 *
 * @example
 * const history = createInMemoryAIHistory({ limit: 50 });
 */
export function createInMemoryAIHistory(options: { readonly limit?: number } = {}): AIHistory {
  const limit = Math.max(1, options.limit ?? 50);
  let entries: AIHistoryEntry[] = [];
  return {
    record(entry) {
      entries = [...entries, entry].slice(-limit);
    },
    updateStatus(requestId, status) {
      entries = entries.map((entry) =>
        entry.requestId === requestId ? { ...entry, status } : entry,
      );
    },
    list: () => entries,
    clear() {
      entries = [];
    },
  };
}
